import React, { useEffect } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";

import Stack from "@mui/material/Stack";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import LogoutIcon from "@mui/icons-material/Logout";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { useThemeMode } from "./ThemeContext";

import AppContext, { AppContextType } from "./AppContext";
import Admin from "./Admin";
import Login from "./Login";
import ChatPage from "./ChatPage";
import FaqPage from "./FaqPage";
import ProtectedRoute from "./ProtectedRoute";
import Callback from "./Callback"; // Import the new Callback component
import PublicLogout from "./PublicLogout";

// Define a type for the user profile from Cognito, adjust as needed
interface UserProfile {
  email?: string;
  username?: string; // Cognito's own username attribute
  preferred_username?: string; // Standard OIDC claim, often mapped to username
  "cognito:username"?: string; // Cognito-specific claim for the username
  "cognito:groups"?: string[]; // Add Cognito groups, typically an array of strings
  [key: string]: any; // Allow other properties
}

function App() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { mode, toggleTheme } = useThemeMode();

  // The user state from AppContext will now be derived from auth.user?.profile
  // We can simplify the local user state or remove it if AppContext directly uses auth.user
  const [userProfile, setUserProfile] = React.useState<UserProfile | null>(null);
  const [surveyResult, setSurveyResult] = React.useState<any[]>([]);

  // Lifted state for chat persistence
  const [chatMessages, setChatMessages] = React.useState<AppContextType['chatMessages']>([]);
  const [chatHistory, setChatHistory] = React.useState<any[]>([]);
  const [submittedFeedback, setSubmittedFeedback] = React.useState<number[]>([]);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user?.profile) {
      // console.log("User Profile from auth.user.profile:", auth.user.profile); // Log the profile object
      // Extract cognito:groups if it exists
      const profileData = auth.user.profile as UserProfile;
      const userGroups = auth.user.profile["cognito:groups"] as string[] | undefined;

      setUserProfile({
        ...profileData,
        "cognito:groups": userGroups,
      });
    } else {
      setUserProfile(null);
    }
  }, [auth.isAuthenticated, auth.user?.profile]);

  function handleNavigateHome() {
    navigate(`/`);
  }

  function handleNavigatePrompt() {
    navigate(`/prompt`);
  }

  function handleNavigateAdmin() {
    navigate(`/admin`);
  }

  function handleNavigateFaq() {
    navigate(`/faq`);
  }

  function handleLogin() {
    // Redirects to Cognito Hosted UI
    auth.signinRedirect();
  }

  function handleLogout() {
    navigate("/public-logout");
  }

  if (auth.isLoading) {
    return <Stack sx={{ height: "100%", justifyContent: "center", alignItems: "center" }}><Typography>Loading...</Typography></Stack>;
  }

  if (auth.error) {
    return <Stack sx={{ height: "100%", justifyContent: "center", alignItems: "center" }}><Typography color="error">Authentication Error: {auth.error.message}</Typography></Stack>;
  }

  const contextValue: AppContextType = {
    user: userProfile,
    setUser: setUserProfile,
    surveyResult,
    setSurveyResult,
    chatMessages,
    setChatMessages,
    chatHistory,
    setChatHistory,
    submittedFeedback,
    setSubmittedFeedback
  };

  return (
    <Stack sx={{ height: "100vh", "@supports (height: 100dvh)": { height: "100dvh" }, overflow: "hidden" }}>
      <AppContext.Provider value={contextValue}>
        <AppBar
          position="fixed"
          elevation={1} // Adds a subtle modern shadow
          sx={{
            backgroundColor: "background.paper", // Clean white/light background
            color: "text.primary", // Dark text for contrast
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between" }}>
            {/* LEFT SIDE: Logo and Navigation */}
            <Stack direction="row" alignItems="center" spacing={4}>
              
              {/* Brand / Logo Area */}
              <Stack 
                direction="row" 
                alignItems="center" 
                spacing={1.5} 
                onClick={() => handleNavigateHome()}
                sx={{ 
                  cursor: "pointer",
                  transition: "opacity 0.2s",
                  "&:hover": { opacity: 0.8 } 
                }}
              >
                <img 
                  src="/icon.png" 
                  alt="ETS Logo" 
                  style={{ height: "32px", width: "auto", display: "block", borderRadius: '6px', padding: '2px', backgroundColor: 'white' }}
                />
                <Typography
                  variant="h6"
                  noWrap
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "-0.5px",
                    color: "text.primary",
                    lineHeight: 1
                  }}
                >
                  ETS Bot
                </Typography>
              </Stack>

              {/* Help icon next to brand */}
              {auth.isAuthenticated && (
                <Tooltip title="Help & FAQ">
                  <IconButton
                    onClick={() => handleNavigateFaq()}
                    size="small"
                    sx={{ color: "text.secondary", ml: -2.5 }}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Navigation Links */}
              <Box sx={{ display: "flex", gap: 1 }}>
                {auth.isAuthenticated && userProfile?.["cognito:groups"]?.includes('admin') && (
                  <Button
                    color="inherit"
                    onClick={() => handleNavigateAdmin()}
                    sx={{ textTransform: "none", fontWeight: 600, fontSize: "1rem", display: { xs: "none", md: "inline-flex" } }}
                  >
                    Admin
                  </Button>
                )}
              </Box>
            </Stack>

            {/* RIGHT SIDE: Authentication & User */}
            <Stack direction="row" alignItems="center" spacing={2}>
              {!auth.isAuthenticated ? (
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleLogin}
                  disableElevation
                  sx={{ borderRadius: "20px", textTransform: "none" }} // Pill-shaped, modern button
                >
                  Login
                </Button>
              ) : (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                    Hi, {
                      userProfile?.preferred_username ||
                      userProfile?.username ||
                      userProfile?.["cognito:username"] ||
                      userProfile?.email ||
                      'User'
                    }
                  </Typography>
                  <Tooltip title={mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
                    <IconButton
                      onClick={toggleTheme}
                      color="inherit"
                      sx={{
                        backgroundColor: 'action.hover',
                        '&:hover': { backgroundColor: 'action.selected' }
                      }}
                    >
                      {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Logout">
                    <IconButton
                      onClick={handleLogout}
                      color="inherit"
                      sx={{
                        backgroundColor: 'action.hover',
                        '&:hover': { backgroundColor: 'action.selected' }
                      }}
                    >
                      <LogoutIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          </Toolbar>
        </AppBar>
        
        {/* Add padding to account for the fixed AppBar, preventing content from being hidden behind it */}
        <Box component="main" sx={{ flexGrow: 1, pt: "64px", height: "calc(100vh - 64px)", "@supports (height: 100dvh)": { height: "calc(100dvh - 64px)" }, display: "flex", flexDirection: "column" }}>
          <Routes>
            <Route path="/login/" element={<Login />}></Route>
            <Route path="/callback" element={<Callback />} />
            <Route path="/public-logout" element={<PublicLogout />} />
            
            {/* redirect route */}
            <Route path="/prompt" element={<Navigate to="/" replace />} />

            {/* Routes wrapped by ProtectedRoute will require authentication */}
            <Route element={<ProtectedRoute />}>
              {/* ChatPage is now the default Home route! */}
              <Route path="/" element={<ChatPage />} />
              <Route path="/faq" element={<FaqPage />} />
            </Route>
            <Route element={<ProtectedRoute requiredGroup="admin" />}>
              <Route path="/admin/" element={<Admin />} />
            </Route>
          </Routes>
        </Box>
      </AppContext.Provider>
    </Stack>
  );
}

export default App;
